import { useMemo, useState } from 'react';
import {
  Box,
  Calculator,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  Layers3,
  Lock,
  Palette,
  Plus,
  Settings2,
  Trash2,
  Unlock,
  Video,
} from 'lucide-react';
import type { ModelEntity, SavedScene } from '../model/types';
import { calculateQuantities } from '../model/quantities';
import { formatArea, formatLength, formatVolume } from '../utils/units';
import { useStudioStore, type SidebarTab } from '../state/useStudioStore';
import { NumberField } from './NumberField';

interface InspectorProps {
  onSaveScene: () => void;
  onRestoreScene: (scene: SavedScene) => void;
}

const MATERIALS = [
  { name: 'Canvas green', color: '#62d995' },
  { name: 'Warm white', color: '#dce2df' },
  { name: 'Concrete', color: '#9ca8a2' },
  { name: 'Charcoal', color: '#405048' },
  { name: 'Timber', color: '#b5835a' },
  { name: 'Brick', color: '#b86548' },
  { name: 'Glass', color: '#76b5cf' },
  { name: 'Sandstone', color: '#c9ad7e' },
  { name: 'Steel', color: '#6c7781' },
  { name: 'Terracotta', color: '#b45f45' },
  { name: 'Navy', color: '#3d536d' },
  { name: 'Black', color: '#202824' },
];

const tabs: Array<{ id: SidebarTab; label: string; icon: typeof Layers3 }> = [
  { id: 'model', label: 'Model', icon: Layers3 },
  { id: 'properties', label: 'Properties', icon: Settings2 },
  { id: 'materials', label: 'Materials', icon: Palette },
  { id: 'quantities', label: 'Quantities', icon: Calculator },
  { id: 'scenes', label: 'Scenes', icon: Video },
];

export function Inspector({ onSaveScene, onRestoreScene }: InspectorProps): JSX.Element | null {
  const open = useStudioStore((state) => state.sidebarOpen);
  const activeTab = useStudioStore((state) => state.sidebarTab);
  const setActiveTab = useStudioStore((state) => state.setSidebarTab);
  if (!open) return null;

  return (
    <aside id="model-inspector" className="inspector" aria-label="Model inspector">
      <div className="inspector-tabs" role="tablist">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            type="button"
            key={id}
            className={activeTab === id ? 'is-active' : ''}
            onClick={() => setActiveTab(id)}
            title={label}
            aria-label={label}
            aria-selected={activeTab === id}
            role="tab"
          >
            <Icon size={18} />
          </button>
        ))}
      </div>
      <div className="inspector-content">
        <header className="panel-heading">
          <span>{tabs.find((tab) => tab.id === activeTab)?.label}</span>
          <small>ConstructCanvas</small>
        </header>
        {activeTab === 'model' ? <ModelPanel /> : null}
        {activeTab === 'properties' ? <PropertiesPanel /> : null}
        {activeTab === 'materials' ? <MaterialsPanel /> : null}
        {activeTab === 'quantities' ? <QuantitiesPanel /> : null}
        {activeTab === 'scenes' ? <ScenesPanel onSave={onSaveScene} onRestore={onRestoreScene} /> : null}
      </div>
    </aside>
  );
}

function ModelPanel(): JSX.Element {
  const document = useStudioStore((state) => state.document);
  const selectedIds = useStudioStore((state) => state.selectedIds);
  const selectEntity = useStudioStore((state) => state.selectEntity);
  const updateEntity = useStudioStore((state) => state.updateEntity);
  const updateLayer = useStudioStore((state) => state.updateLayer);
  const updateSettings = useStudioStore((state) => state.updateSettings);
  const [search, setSearch] = useState('');

  const entities = document.entities.filter((entity) => entity.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="panel-body">
      <section className="panel-section">
        <div className="section-title"><span>Outliner</span><small>{document.entities.length} elements</small></div>
        <input className="panel-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search elements" />
        <div className="entity-tree">
          {entities.map((entity) => (
            <div key={entity.id} className={`entity-row ${selectedIds.includes(entity.id) ? 'is-selected' : ''}`}>
              <button type="button" className="entity-main" onClick={() => selectEntity(entity.id)}>
                <ChevronRight size={14} />
                <KindGlyph kind={entity.kind} />
                <span>{entity.name}</span>
              </button>
              <button type="button" title={entity.visible ? 'Hide' : 'Show'} onClick={() => updateEntity(entity.id, { visible: !entity.visible })}>
                {entity.visible ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              <button type="button" title={entity.locked ? 'Unlock' : 'Lock'} onClick={() => updateEntity(entity.id, { locked: !entity.locked })}>
                {entity.locked ? <Lock size={14} /> : <Unlock size={14} />}
              </button>
            </div>
          ))}
          {!entities.length ? <p className="empty-state">No matching model elements.</p> : null}
        </div>
      </section>

      <section className="panel-section">
        <div className="section-title"><span>Layers</span></div>
        <div className="layer-list">
          {document.layers.map((layer) => (
            <div className="layer-row" key={layer.id}>
              <span className="layer-dot" style={{ background: layer.color }} />
              <span>{layer.name}</span>
              <button type="button" onClick={() => updateLayer(layer.id, { visible: !layer.visible })}>
                {layer.visible ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              <button type="button" onClick={() => updateLayer(layer.id, { locked: !layer.locked })}>
                {layer.locked ? <Lock size={14} /> : <Unlock size={14} />}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="panel-section">
        <div className="section-title"><span>Model settings</span></div>
        <label className="select-field"><span>Units</span>
          <select value={document.settings.units} onChange={(event) => updateSettings({ units: event.target.value as 'metric' | 'imperial' })}>
            <option value="metric">Metric</option>
            <option value="imperial">Imperial</option>
          </select>
        </label>
        <NumberField label="Grid" value={document.settings.gridSize} min={0.01} step={0.05} suffix="m" onCommit={(gridSize) => updateSettings({ gridSize })} />
        <Toggle label="Grid visible" checked={document.settings.showGrid} onChange={(showGrid) => updateSettings({ showGrid })} />
        <Toggle label="Axes visible" checked={document.settings.showAxes} onChange={(showAxes) => updateSettings({ showAxes })} />
        <Toggle label="Shadows" checked={document.settings.shadows} onChange={(shadows) => updateSettings({ shadows })} />
        <label className="color-field"><span>Background</span><input type="color" value={document.settings.background} onChange={(event) => updateSettings({ background: event.target.value })} /></label>
      </section>
    </div>
  );
}

function KindGlyph({ kind }: { kind: ModelEntity['kind'] }): JSX.Element {
  return <Box size={14} className={`kind-glyph kind-${kind}`} />;
}

function PropertiesPanel(): JSX.Element {
  const document = useStudioStore((state) => state.document);
  const selectedIds = useStudioStore((state) => state.selectedIds);
  const updateEntity = useStudioStore((state) => state.updateEntity);
  const duplicateSelected = useStudioStore((state) => state.duplicateSelected);
  const deleteEntities = useStudioStore((state) => state.deleteEntities);
  const deleteOpening = useStudioStore((state) => state.deleteOpening);
  const updateSettings = useStudioStore((state) => state.updateSettings);

  if (selectedIds.length === 0) {
    return (
      <div className="panel-body">
        <div className="selection-empty">
          <span className="empty-icon"><Settings2 size={26} /></span>
          <h3>No selection</h3>
          <p>Select a wall, slab, box or line to edit exact geometry and construction data.</p>
        </div>
        <section className="panel-section">
          <div className="section-title"><span>Construction defaults</span></div>
          <NumberField label="Wall height" value={document.settings.wallHeight} min={0.1} suffix="m" onCommit={(wallHeight) => updateSettings({ wallHeight })} />
          <NumberField label="Wall thickness" value={document.settings.wallThickness} min={0.03} suffix="m" onCommit={(wallThickness) => updateSettings({ wallThickness })} />
          <NumberField label="Slab thickness" value={document.settings.slabThickness} min={0.03} suffix="m" onCommit={(slabThickness) => updateSettings({ slabThickness })} />
          <NumberField label="Door width" value={document.settings.doorWidth} min={0.3} suffix="m" onCommit={(doorWidth) => updateSettings({ doorWidth })} />
          <NumberField label="Window width" value={document.settings.windowWidth} min={0.2} suffix="m" onCommit={(windowWidth) => updateSettings({ windowWidth })} />
        </section>
      </div>
    );
  }

  if (selectedIds.length > 1) {
    return (
      <div className="panel-body">
        <div className="selection-empty">
          <span className="empty-icon"><Layers3 size={26} /></span>
          <h3>{selectedIds.length} elements selected</h3>
          <p>Use Move, Rotate or Scale to transform the primary selection. Duplicate or delete applies to all selected elements.</p>
          <div className="button-row">
            <button type="button" className="secondary-button" onClick={duplicateSelected}><Copy size={15} />Duplicate</button>
            <button type="button" className="danger-button" onClick={() => deleteEntities(selectedIds)}><Trash2 size={15} />Delete</button>
          </div>
        </div>
      </div>
    );
  }

  const entity = document.entities.find((item) => item.id === selectedIds[0]);
  if (!entity) return <div className="panel-body"><p className="empty-state">Selection no longer exists.</p></div>;

  const setDimension = (key: keyof ModelEntity['dimensions'], value: number) => {
    const dimensions = { ...entity.dimensions, [key]: value };
    const position = [...entity.position] as [number, number, number];
    if (key === 'height') {
      const base = entity.position[1] - entity.dimensions.height / 2;
      position[1] = base + value / 2;
    }
    updateEntity(entity.id, { dimensions, position });
  };
  const setPosition = (index: number, value: number) => {
    const position = [...entity.position] as [number, number, number];
    position[index] = value;
    updateEntity(entity.id, { position });
  };
  const setRotation = (index: number, degrees: number) => {
    const rotation = [...entity.rotation] as [number, number, number];
    rotation[index] = degrees * Math.PI / 180;
    updateEntity(entity.id, { rotation });
  };
  const setScale = (index: number, value: number) => {
    const scale = [...entity.scale] as [number, number, number];
    scale[index] = value;
    updateEntity(entity.id, { scale });
  };

  return (
    <div className="panel-body">
      <section className="panel-section inspector-identity">
        <span className="entity-kind-label">{entity.kind}</span>
        <input className="entity-name-input" value={entity.name} onChange={(event) => updateEntity(entity.id, { name: event.target.value })} />
        <div className="button-row compact-row">
          <button type="button" className="secondary-button" onClick={duplicateSelected}><Copy size={14} />Duplicate</button>
          <button type="button" className="danger-button" onClick={() => deleteEntities([entity.id])}><Trash2 size={14} />Delete</button>
        </div>
      </section>

      <section className="panel-section">
        <div className="section-title"><span>Dimensions</span><small>metres</small></div>
        <NumberField label="Width / length" value={entity.dimensions.width} min={0.001} suffix="m" onCommit={(value) => setDimension('width', value)} disabled={entity.locked} />
        <NumberField label="Height" value={entity.dimensions.height} min={0.001} suffix="m" onCommit={(value) => setDimension('height', value)} disabled={entity.locked} />
        <NumberField label="Depth / thickness" value={entity.dimensions.depth} min={0.001} suffix="m" onCommit={(value) => setDimension('depth', value)} disabled={entity.locked || entity.kind === 'line'} />
      </section>

      <section className="panel-section">
        <div className="section-title"><span>Transform</span></div>
        <div className="field-grid three">
          {(['X', 'Y', 'Z'] as const).map((axis, index) => <NumberField key={`p${axis}`} label={`P${axis}`} value={entity.position[index]} suffix="m" onCommit={(value) => setPosition(index, value)} disabled={entity.locked} />)}
        </div>
        <div className="field-grid three">
          {(['X', 'Y', 'Z'] as const).map((axis, index) => <NumberField key={`r${axis}`} label={`R${axis}`} value={entity.rotation[index] * 180 / Math.PI} suffix="°" decimals={1} onCommit={(value) => setRotation(index, value)} disabled={entity.locked} />)}
        </div>
        <div className="field-grid three">
          {(['X', 'Y', 'Z'] as const).map((axis, index) => <NumberField key={`s${axis}`} label={`S${axis}`} value={entity.scale[index]} step={0.05} decimals={2} min={0.01} onCommit={(value) => setScale(index, value)} disabled={entity.locked} />)}
        </div>
      </section>

      <section className="panel-section">
        <div className="section-title"><span>Appearance & organisation</span></div>
        <label className="color-field"><span>Colour</span><input type="color" value={entity.color} disabled={entity.locked} onChange={(event) => updateEntity(entity.id, { color: event.target.value })} /></label>
        <NumberField label="Opacity" value={entity.opacity} min={0.05} max={1} step={0.05} decimals={2} onCommit={(opacity) => updateEntity(entity.id, { opacity })} disabled={entity.locked} />
        <label className="select-field"><span>Layer</span>
          <select value={entity.layerId} disabled={entity.locked} onChange={(event) => updateEntity(entity.id, { layerId: event.target.value })}>
            {document.layers.map((layer) => <option key={layer.id} value={layer.id}>{layer.name}</option>)}
          </select>
        </label>
        <Toggle label="Visible" checked={entity.visible} onChange={(visible) => updateEntity(entity.id, { visible })} />
        <Toggle label="Locked" checked={entity.locked} onChange={(locked) => updateEntity(entity.id, { locked })} />
      </section>

      {entity.kind === 'wall' ? (
        <section className="panel-section">
          <div className="section-title"><span>Hosted openings</span><small>{entity.openings?.length ?? 0}</small></div>
          {(entity.openings ?? []).map((opening) => (
            <div className="opening-row" key={opening.id}>
              <span className={`opening-kind ${opening.kind}`}>{opening.kind}</span>
              <span>{opening.width.toFixed(2)} × {opening.height.toFixed(2)} m</span>
              <button type="button" title="Remove opening" disabled={entity.locked} onClick={() => deleteOpening(entity.id, opening.id)}><Trash2 size={14} /></button>
            </div>
          ))}
          {!entity.openings?.length ? <p className="empty-state">Use the Door or Window tool and click this wall.</p> : null}
        </section>
      ) : null}

      <section className="panel-section">
        <div className="section-title"><span>Data</span></div>
        <dl className="metadata-list">
          <div><dt>ID</dt><dd title={entity.id}>{entity.id.slice(0, 18)}…</dd></div>
          {Object.entries(entity.metadata).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>)}
        </dl>
      </section>
    </div>
  );
}

function MaterialsPanel(): JSX.Element {
  const paintColor = useStudioStore((state) => state.paintColor);
  const setPaintColor = useStudioStore((state) => state.setPaintColor);
  const setActiveTool = useStudioStore((state) => state.setActiveTool);
  const selectedIds = useStudioStore((state) => state.selectedIds);
  const updateEntity = useStudioStore((state) => state.updateEntity);

  const apply = (color: string) => {
    setPaintColor(color);
    selectedIds.forEach((id) => updateEntity(id, { color }));
  };

  return (
    <div className="panel-body">
      <section className="panel-section">
        <div className="material-current">
          <span className="large-swatch" style={{ background: paintColor }} />
          <div><strong>Active paint</strong><small>{paintColor.toUpperCase()}</small></div>
          <button type="button" className="secondary-button" onClick={() => setActiveTool('paint')}>Paint tool</button>
        </div>
      </section>
      <section className="panel-section">
        <div className="section-title"><span>Construction palette</span></div>
        <div className="swatch-grid">
          {MATERIALS.map((material) => (
            <button
              type="button"
              key={material.color}
              className={paintColor === material.color ? 'is-active' : ''}
              title={`${material.name} — click to apply to selection`}
              onClick={() => apply(material.color)}
            >
              <span style={{ background: material.color }} />
              <small>{material.name}</small>
            </button>
          ))}
        </div>
      </section>
      <section className="panel-section">
        <div className="section-title"><span>Custom colour</span></div>
        <label className="color-field"><span>Paint colour</span><input type="color" value={paintColor} onChange={(event) => setPaintColor(event.target.value)} /></label>
        <button type="button" className="primary-button" disabled={!selectedIds.length} onClick={() => selectedIds.forEach((id) => updateEntity(id, { color: paintColor }))}>Apply to selection</button>
      </section>
    </div>
  );
}

function QuantitiesPanel(): JSX.Element {
  const document = useStudioStore((state) => state.document);
  const quantities = useMemo(() => calculateQuantities(document), [document]);
  const rows = [
    ['Elements', String(quantities.entityCount)],
    ['Walls', String(quantities.wallCount)],
    ['Wall length', formatLength(quantities.wallLength, document.settings.units)],
    ['Gross wall area', formatArea(quantities.grossWallArea, document.settings.units)],
    ['Opening area', formatArea(quantities.openingArea, document.settings.units)],
    ['Net wall area', formatArea(quantities.netWallArea, document.settings.units)],
    ['Slab area', formatArea(quantities.slabArea, document.settings.units)],
    ['Slab volume', formatVolume(quantities.slabVolume, document.settings.units)],
    ['Object volume', formatVolume(quantities.boxVolume, document.settings.units)],
  ];

  return (
    <div className="panel-body">
      <section className="quantity-hero">
        <small>Live model takeoff</small>
        <strong>{formatArea(quantities.slabArea, document.settings.units)}</strong>
        <span>total slab footprint</span>
      </section>
      <section className="panel-section">
        <div className="section-title"><span>Summary</span><small>visible geometry</small></div>
        <dl className="quantity-list">
          {rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl>
      </section>
      <section className="panel-note">
        <strong>Estimate-ready semantics</strong>
        <p>Walls and hosted openings retain construction identity, allowing downstream material, cost and carbon calculations.</p>
      </section>
    </div>
  );
}

function ScenesPanel({ onSave, onRestore }: { onSave: () => void; onRestore: (scene: SavedScene) => void }): JSX.Element {
  const scenes = useStudioStore((state) => state.document.scenes);
  const deleteScene = useStudioStore((state) => state.deleteScene);

  return (
    <div className="panel-body">
      <section className="panel-section">
        <button type="button" className="primary-button" onClick={onSave}><Plus size={15} />Save current view</button>
      </section>
      <section className="panel-section">
        <div className="section-title"><span>Saved scenes</span><small>{scenes.length}</small></div>
        <div className="scene-list">
          {scenes.map((scene) => (
            <div className="scene-card" key={scene.id}>
              <button type="button" className="scene-main" onClick={() => onRestore(scene)}>
                <span className="scene-thumbnail"><Video size={20} /></span>
                <span><strong>{scene.name}</strong><small>{scene.projection}</small></span>
              </button>
              <button type="button" title="Delete scene" onClick={() => deleteScene(scene.id)}><Trash2 size={14} /></button>
            </div>
          ))}
          {!scenes.length ? <p className="empty-state">Save named camera positions for presentations and reviews.</p> : null}
        </div>
      </section>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }): JSX.Element {
  return (
    <label className="toggle-field">
      <span>{label}</span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <i aria-hidden="true" />
    </label>
  );
}
